import { useRef, useState } from "react";
import { Volume2, Loader2, Square, Mic, MicOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// 🔊 Read-aloud button — speaks the given text (literacy assistance).
export function ListenButton({ text, label = "Listen", className = "", voice = "nova" }) {
    const [status, setStatus] = useState("idle"); // idle | loading | playing
    const audioRef = useRef(null);

    const stop = () => {
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        setStatus("idle");
    };

    const play = async () => {
        if (status === "playing") return stop();
        const clean = (text || "").trim();
        if (!clean) return;
        setStatus("loading");
        try {
            const r = await api.post("/inmate/voice/tts", { text: clean.slice(0, 4096), voice });
            const audio = new Audio(`data:${r.data.mime || "audio/mp3"};base64,${r.data.audio_base64}`);
            audioRef.current = audio;
            audio.onended = () => setStatus("idle");
            audio.onerror = () => { setStatus("idle"); toast.error("Couldn't play audio."); };
            await audio.play();
            setStatus("playing");
        } catch {
            setStatus("idle");
            toast.error("Read-aloud isn't available right now.");
        }
    };

    return (
        <button type="button" onClick={play} data-testid="listen-btn" aria-label={status === "playing" ? "Stop" : "Read aloud"}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${status === "playing" ? "bg-phoenix-navy text-white border-phoenix-navy" : "bg-white text-phoenix-navy border-phoenix-navy/30 hover:bg-phoenix-soft/30"} ${className}`}>
            {status === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : status === "playing" ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            {status === "playing" ? "Stop" : label}
        </button>
    );
}

// 🎙️ Voice-to-text dictation — records the mic and returns transcribed text.
export function VoiceInput({ onTranscript, label = "Speak", className = "" }) {
    const [status, setStatus] = useState("idle"); // idle | recording | processing
    const recRef = useRef(null);
    const chunksRef = useRef([]);

    const start = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                if (!blob.size) { setStatus("idle"); return; }
                setStatus("processing");
                try {
                    const fd = new FormData();
                    fd.append("file", blob, "recording.webm");
                    fd.append("language", "en");
                    const r = await api.post("/inmate/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    const t = (r.data.text || "").trim();
                    if (t) onTranscript(t);
                    else toast.message("Didn't catch that — try again.");
                } catch {
                    toast.error("Couldn't transcribe — try again.");
                } finally { setStatus("idle"); }
            };
            recRef.current = rec;
            rec.start();
            setStatus("recording");
        } catch {
            toast.error("Microphone access is needed to use voice.");
        }
    };

    const stop = () => { if (recRef.current && status === "recording") recRef.current.stop(); };
    const toggle = () => (status === "recording" ? stop() : status === "idle" ? start() : null);

    return (
        <button type="button" onClick={toggle} data-testid="voice-input-btn" disabled={status === "processing"}
            aria-label={status === "recording" ? "Stop recording" : "Record voice"}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${status === "recording" ? "bg-red-500 text-white border-red-500 animate-pulse" : "bg-white text-phoenix-navy border-phoenix-navy/30 hover:bg-phoenix-soft/30"} ${className}`}>
            {status === "processing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : status === "recording" ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            {status === "processing" ? "Transcribing…" : status === "recording" ? "Stop" : label}
        </button>
    );
}
